type WithClientId<T> = T & { clientId?: string };

export type QuestionnaireChoiceForm = WithClientId<SpApi.Studio.IQuestionnaireChoicePost>;

export type QuestionnaireQuestionForm = WithClientId<
    Omit<SpApi.Studio.IQuestionnaireQuestionPost, 'choices' | 'responsePlaceholder'>
> & {
    choices?: QuestionnaireChoiceForm[];
    responsePlaceholder?: string | null;
};

export type QuestionnairePageForm = WithClientId<
    Omit<SpApi.Studio.IQuestionnairePagePost, 'questions'>
> & { questions: QuestionnaireQuestionForm[] };

/**
 * Minimal structural types that both Questionnaire and QuestionnaireTemplate
 * model variants satisfy. Use these in shared UI components so they accept
 * either flavour without casts.
 */
export type BaseChoiceForm = {
    id?: number;
    clientId?: string;
    choiceText: string;
    displayOrder: number;
};

export type BaseQuestionForm = {
    id?: number;
    clientId?: string;
    questionText: string;
    required: boolean;
    displayOrder: number;
    responseType: SpApi.Studio.QuestionnaireResponseType;
    responsePlaceholder?: string | null;
    choices?: BaseChoiceForm[];
};

export type BasePageForm = {
    id?: number;
    clientId?: string;
    name: string;
    displayOrder: number;
    questions: BaseQuestionForm[];
};

export const getDefaultPageFormValue = (): BasePageForm => ({
    clientId: crypto.randomUUID(),
    name: 'Page 1',
    displayOrder: 1,
    questions: [
        {
            clientId: crypto.randomUUID(),
            questionText: '',
            required: false,
            displayOrder: 1,
            responseType: 'text_short' as const,
            responsePlaceholder: null
        }
    ]
});

// Helper function to get a unique identifier for an entity (id or clientId)
export const getEntityId = (entity: { id?: number; clientId?: string }): string => {
    return entity.id?.toString() ?? entity.clientId ?? crypto.randomUUID();
};

export type QuestionnaireCreateFormValues = SpApi.Studio.IQuestionnaireCreatePayload & {
    contact: Pick<SpApi.Studio.IContact, 'id' | 'name'>;
};

export type QuestionnaireUpdateFormValues = Omit<
    SpApi.Studio.IQuestionnaireUpdatePayload,
    'dueDate' | 'pages'
> & {
    dueDate: Date | null;
    contact: Pick<SpApi.Studio.IContact, 'id' | 'name'>;
    pages: QuestionnairePageForm[];
};

export const responseTypeTranslationKeys: Record<SpApi.Studio.QuestionnaireResponseType, string> = {
    text_short: 'questionType.textShort',
    text_long: 'questionType.textLong',
    choices_dropdown: 'questionType.choicesDropdown',
    choices_radio: 'questionType.choicesRadio',
    choices_checkboxes: 'questionType.choicesCheckboxes'
};

export const hasChoices = (responseType: SpApi.Studio.QuestionnaireResponseType) => {
    return (
        responseType === 'choices_dropdown' ||
        responseType === 'choices_radio' ||
        responseType === 'choices_checkboxes'
    );
};

export const choiceSeparator = '|';

export interface QuestionValidationErrors {
    questionText?: string;
    choices?: string;
}

export interface PageValidationErrors {
    name?: string;
    questions: Record<string, QuestionValidationErrors>;
}

export interface ValidationErrors {
    pages: Record<string, PageValidationErrors>;
}

export function validatePages(
    pages: BasePageForm[],
    t: (key: string, params?: Record<string, unknown>) => string
): { errors: ValidationErrors; firstErrorElementId: string | null; hasErrors: boolean } {
    const errors: ValidationErrors = { pages: {} };
    let hasErrors = false;
    let firstErrorElementId: string | null = null;

    for (const [pageIndex, page] of Array.from(pages.entries())) {
        const pageId = getEntityId(page);
        const pageErrors: PageValidationErrors = { questions: {} };

        if (!page.name.trim()) {
            pageErrors.name = t('errors.pageNameRequired');
            hasErrors = true;
            firstErrorElementId ??= `questionnaire-page-${pageId}-name`;
        }

        for (const [questionIndex, question] of Array.from(page.questions.entries())) {
            const questionId = getEntityId(question);
            const questionErrors: QuestionValidationErrors = {};

            if (!question.questionText.trim()) {
                questionErrors.questionText = t('errors.questionTextRequired', {
                    pageNumber: pageIndex + 1,
                    questionNumber: questionIndex + 1
                });
                hasErrors = true;
                firstErrorElementId ??= `questionnaire-page-${pageId}-question-${questionId}-question-text`;
            }

            if (hasChoices(question.responseType)) {
                const choices = question.choices ?? [];
                const firstEmptyChoice = choices.find((choice) => choice.choiceText.trim() === '');

                if (!choices.length || firstEmptyChoice) {
                    questionErrors.choices = t('errors.choicesRequired', {
                        pageNumber: pageIndex + 1,
                        questionNumber: questionIndex + 1
                    });
                    hasErrors = true;

                    if (!firstErrorElementId) {
                        const targetChoice = firstEmptyChoice ?? choices[0];

                        if (targetChoice) {
                            firstErrorElementId = `questionnaire-page-${pageId}-question-${questionId}-choice-${getEntityId(
                                targetChoice
                            )}-choice-text`;
                        }
                    }
                }
            }

            if (questionErrors.questionText || questionErrors.choices) {
                pageErrors.questions[questionId] = questionErrors;
            }
        }

        if (pageErrors.name || Object.keys(pageErrors.questions).length > 0) {
            errors.pages[pageId] = pageErrors;
        }
    }

    return { errors, firstErrorElementId, hasErrors };
}

export const formatDateForApi = (date: Date | null | undefined) => {
    if (!date || Number.isNaN(date.getTime())) {
        return undefined;
    }

    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${date.getFullYear()}-${month}-${day}`;
};

export const isQuestionnaireEditable = (
    questionnaire: SpApi.Studio.IQuestionnaire | SpApi.Studio.IQuestionnaireListItem
) => {
    const { questionnaireStatus } = questionnaire;
    return questionnaireStatus === 'draft' || questionnaireStatus === 'pending';
};
